import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogBody, FormDialogFooter } from '../../components/ui/form-dialog';
import React, { useEffect, useRef, useState } from 'react';
import { Layers, AlertCircle, Save } from 'lucide-react';
import type {
  CelulaKanbanEntity,
  EstufaEntity,
  FamiliaCor,
  LimiteWip,
  PortePeca,
  TipoTinta,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';
import { Checkbox } from '../../components/ui/checkbox';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LimitesWipEditor } from './limites-wip-editor';
import {
  FAMILIAS_COR,
  PORTES_CABINE,
  TINTAS_CABINE,
  deLinhas,
  formularioDaCelula,
  limitesIguais,
  montarPayloadKanbanCelula,
  paraLinhas,
  podeGravarFormulario,
  servicoAtualForaDaLista,
  validarLinhasLimiteWip,
  validarFatorPerdaTinta,
  validarRegrasCabine,
  type ErroLimiteWip,
  type ErroRegrasCabine,
  type FormularioCelulaKanban,
  type LinhaLimiteWip,
} from '../../lib/kanban/cadastro-kanban';
import { CampoSelect } from '../../components/ui/campo-select';

interface ServicoOpcao {
  id: string;
  codigo: string;
  nome: string;
}

interface CelulaKanbanFormProps {
  celula: CelulaKanbanEntity;
  podeEditar: boolean;
  onClose: () => void;
  onSalvo: () => void;
}


async function mensagemDoServidor(res: Response, padrao: string): Promise<string> {
  const data = (await res.json().catch(() => ({}))) as { message?: string | string[] };
  const mensagem = Array.isArray(data.message) ? data.message.join(' ') : data.message;
  return mensagem || padrao;
}

function alternar<T extends string>(lista: readonly T[], valor: T, marcado: boolean): T[] {
  const semValor = lista.filter((item) => item !== valor);
  return marcado ? [...semValor, valor] : semValor;
}

export const CelulaKanbanForm: React.FC<CelulaKanbanFormProps> = ({ celula, podeEditar, onClose, onSalvo }) => {
  // Linha de base do que ja esta gravado: atualizada a cada parte salva com sucesso, para
  // que salvar de novo reenvie so o que ainda difere.
  const [celulaBase, setCelulaBase] = useState<CelulaKanbanEntity>(celula);
  const [limitesBase, setLimitesBase] = useState<LimiteWip[]>([]);
  const [form, setForm] = useState<FormularioCelulaKanban>(() => formularioDaCelula(celula));
  const [ativa, setAtiva] = useState<boolean>(celula.ativo);
  const [linhas, setLinhas] = useState<LinhaLimiteWip[]>([]);
  const [erroLimite, setErroLimite] = useState<ErroLimiteWip | null>(null);
  const gravouAlgo = useRef(false);
  const [servicos, setServicos] = useState<ServicoOpcao[]>([]);
  const [estufas, setEstufas] = useState<EstufaEntity[]>([]);
  const [loading, setLoading] = useState(true);
  // WR-GAP-01: so com a carga completa (servicos, estufas e limites) o formulario e editavel;
  // sem ela o PUT de limites (substituicao total) apagaria os limites gravados.
  const [cargaOk, setCargaOk] = useState(false);
  const [tentativaCarga, setTentativaCarga] = useState(0);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroRegras, setErroRegras] = useState<ErroRegrasCabine | null>(null);
  const [erroFator, setErroFator] = useState<string | null>(null);
  const primeiraTintaRef = useRef<HTMLButtonElement>(null);
  const primeiraFamiliaRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      setLoading(true);
      setCargaOk(false);
      try {
        const [resServicos, resEstufas, resLimites] = await Promise.all([
          apiFetch<{ data: ServicoOpcao[] }>('/api/v1/cadastros/servicos?apenasAtivos=true'),
          apiFetch<{ data: EstufaEntity[] }>('/api/v1/planejamento/estufas?apenasAtivas=false'),
          apiFetch<LimiteWip[]>(`/api/v1/planejamento/celulas/${celula.id}/limites-wip`),
        ]);
        if (!ativo) return;
        setServicos(resServicos?.data ?? []);
        setEstufas(resEstufas?.data ?? []);
        const gravados = Array.isArray(resLimites) ? resLimites : [];
        setLimitesBase(gravados.map((l) => ({ unidade: l.unidade, limite: Number(l.limite) })));
        setLinhas(paraLinhas(gravados));
        setErro(null);
        setCargaOk(true);
      } catch {
        if (ativo) setErro('Falha ao carregar serviços, estufas e limites de WIP.');
      } finally {
        if (ativo) setLoading(false);
      }
    };
    void carregar();
    return () => {
      ativo = false;
    };
  }, [celula.id, tentativaCarga]);

  const atualizar = (parcial: Partial<FormularioCelulaKanban>) => {
    setErroRegras(null);
    setForm((atual) => ({ ...atual, ...parcial }));
  };

  // Fechar depois de uma gravacao parcial precisa atualizar a lista por tras do modal.
  const fechar = () => {
    if (gravouAlgo.current) onSalvo();
    else onClose();
  };

  const handleSalvar = async () => {
    if (!podeGravarFormulario({ loading, cargaOk, saving })) return;
    setErro(null);
    // KAN-10 (empty): cabine sem tinta/familia nunca seria sugerida. So vale ao editar as
    // regras (WR-GAP-02). O erro fica junto do campo, o Salvar continua habilitado e o foco
    // vai para o primeiro campo invalido.
    const invalido = validarRegrasCabine(form, celulaBase);
    setErroRegras(invalido);
    if (invalido) {
      (invalido.campo === 'tintasAceitas' ? primeiraTintaRef : primeiraFamiliaRef).current?.focus();
      return;
    }

    // Plano 17.3-11 (D-10): fator de perda de tinta valido antes de qualquer gravacao.
    const invalidoFator = validarFatorPerdaTinta(form.fatorPerdaTinta);
    setErroFator(invalidoFator);
    if (invalidoFator) {
      document.getElementById('celula-kanban-fator-perda-tinta')?.focus();
      return;
    }

    const erroLinha = validarLinhasLimiteWip(linhas);
    setErroLimite(erroLinha);
    if (erroLinha) {
      document.getElementById(`celula-kanban-limite-${erroLinha.indice}`)?.focus();
      return;
    }

    const payload = montarPayloadKanbanCelula(celulaBase, form);
    const limitesNovos = deLinhas(linhas);
    const limitesMudaram = !limitesIguais(limitesBase, limitesNovos);
    const ativacaoMudou = ativa !== celulaBase.ativo;
    if (payload === null && !limitesMudaram && !ativacaoMudou) {
      fechar();
      return;
    }

    setSaving(true);
    // O que ja foi gravado nesta rodada: nomeado na mensagem se uma parte seguinte falhar.
    const gravadas: string[] = [];
    const naoGravou = (parteNaoGravada: string, mensagem: string) =>
      gravadas.length > 0
        ? `${gravadas.join(' e ')} ${gravadas.length > 1 ? 'salvos' : 'salvo'}, mas ${parteNaoGravada}: ${mensagem}`
        : mensagem;
    try {
      if (payload !== null) {
        const res = await apiMutate(`/api/v1/planejamento/celulas/${celula.id}/kanban`, {
          method: 'PATCH',
          body: payload,
        });
        if (!res.ok) throw new Error(await mensagemDoServidor(res, 'Erro ao atualizar Kanban da Célula'));
        const gravada = (await res.json().catch(() => null)) as Partial<CelulaKanbanEntity> | null;
        setCelulaBase((atual) => ({ ...atual, ...payload, ...(gravada ?? {}) }));
        gravouAlgo.current = true;
        gravadas.push('Processo e cabine');
      }

      if (limitesMudaram) {
        let res: Response;
        try {
          res = await apiMutate(`/api/v1/planejamento/celulas/${celula.id}/limites-wip`, {
            method: 'PUT',
            body: { limites: limitesNovos },
          });
        } catch (err) {
          throw new Error(naoGravou('os limites de WIP não foram gravados', err instanceof Error ? err.message : String(err)));
        }
        if (!res.ok) {
          throw new Error(naoGravou('os limites de WIP não foram gravados', await mensagemDoServidor(res, 'Erro ao gravar limites de WIP')));
        }
        setLimitesBase(limitesNovos);
        gravouAlgo.current = true;
        gravadas.push('Limites de WIP');
      }

      if (ativacaoMudou) {
        // D-46: ativar/desativar tem rota propria (PATCH /kanban nao mexe em `ativo`).
        const rotaAtivacao = ativa
          ? `/api/v1/planejamento/celulas/${celula.id}/ativar`
          : `/api/v1/planejamento/celulas/${celula.id}/desativar`;
        let res: Response;
        try {
          res = await apiMutate(rotaAtivacao, { method: 'PATCH' });
        } catch (err) {
          throw new Error(naoGravou('a ativação não foi gravada', err instanceof Error ? err.message : String(err)));
        }
        if (!res.ok) {
          throw new Error(naoGravou('a ativação não foi gravada', await mensagemDoServidor(res, 'Erro ao alterar a ativação da célula')));
        }
        setCelulaBase((atual) => ({ ...atual, ativo: ativa }));
        gravouAlgo.current = true;
        gravadas.push('Ativação');
      }

      onSalvo();
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  // Estufa inativa so aparece se for a atual da celula (desabilitada, com sufixo).
  const estufasVisiveis = estufas.filter((e) => e.ativo || e.id === celula.estufaId);

  return (
    <FormDialog open onOpenChange={(open) => { if (!open) fechar(); }}>
      <FormDialogContent aria-describedby={undefined}>
        <FormDialogHeader>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-slate-600" />
            <div>
              <FormDialogTitle>Configuração Kanban</FormDialogTitle>
              <p className="text-[11px] font-medium text-slate-500">
                {celula.codigo} — {celula.nome}
              </p>
            </div>
          </div>

        </FormDialogHeader>

        {/* WR-GAP-04: fora da area rolavel, o erro do servidor fica sempre visivel junto do Salvar. */}
        {erro && (
          <div
            role="alert"
            className="mx-5 mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-700 flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        <FormDialogBody className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-500">Carregando...</div>
          ) : !cargaOk ? (
            <div className="text-center py-6">
              <button
                type="button"
                onClick={() => {
                  setErro(null);
                  setTentativaCarga((n) => n + 1);
                }}
                className="px-4 py-2 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                  <Layers className="w-4 h-4 text-slate-500" />
                  Processo
                </h3>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="celula-kanban-ativa"
                      checked={ativa}
                      disabled={!podeEditar}
                      onCheckedChange={(marcado) => setAtiva(marcado === true)}
                    />
                    <Label htmlFor="celula-kanban-ativa" className="text-xs font-semibold text-slate-700">
                      Célula ativa
                    </Label>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Célula inativa não aparece no quadro nem nas sugestões de cabine (ex.: 3ª cabine de líquida/PTFE em
                    implantação).
                  </p>
                  <Label htmlFor="celula-kanban-servico" className="text-xs font-semibold text-slate-700">
                    Serviço / Etapa de Roteiro
                  </Label>
                  <p className="text-[11px] text-slate-500 mb-2">
                    Vincular um Serviço faz com que esta Célula apareça no Quadro Kanban como uma raia da respectiva coluna.
                  </p>
                  <CampoSelect
                    id="celula-kanban-servico"
                    value={form.servicoId}
                    disabled={!podeEditar}
                    onValueChange={(valor) => atualizar({ servicoId: valor })}
                    options={[
                      { value: '', label: 'Sem processo (não aparece no Kanban)' },
                      ...(servicoAtualForaDaLista(celula.servicoId, servicos) && celula.servicoId
                        ? [{ value: celula.servicoId, label: `${celula.servicoNome ?? 'Serviço vinculado'} (inativo)`, disabled: true }]
                        : []),
                      ...servicos.map((s) => ({ value: s.id, label: `${s.codigo} - ${s.nome}` })),
                    ]}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-800 border-b pb-2">Limites de WIP</h3>
                <p className="text-[11px] text-slate-500">
                  O limite só avisa no quadro; nunca impede mover ou iniciar um cartão.
                </p>
                <LimitesWipEditor
                  idPrefixo="celula-kanban"
                  linhas={linhas}
                  onChange={(novas) => {
                    setErroLimite(null);
                    setLinhas(novas);
                  }}
                  erro={erroLimite}
                  desabilitado={!podeEditar}
                />
              </section>

              <section className={`space-y-2 rounded-md border p-3 ${form.ehCabine ? 'border-slate-400 bg-slate-50' : 'border-slate-200'}`}>
                <Label htmlFor="celula-kanban-fator-perda-tinta" className="text-xs font-semibold text-slate-700">
                  Fator de perda de tinta{form.ehCabine ? ' (cabine)' : ''}
                </Label>
                <Input
                  id="celula-kanban-fator-perda-tinta"
                  inputMode="decimal"
                  className="w-32"
                  value={form.fatorPerdaTinta}
                  disabled={!podeEditar}
                  aria-invalid={erroFator !== null}
                  onChange={(evento) => {
                    setErroFator(null);
                    atualizar({ fatorPerdaTinta: evento.target.value });
                  }}
                />
                <p className="text-[11px] text-slate-500">
                  Multiplica o consumo previsto de tinta. 1,000 = sem perda. Use para diferenciar aplicação manual e automática.
                </p>
                {erroFator && (
                  <p role="alert" className="text-[11px] text-rose-700">
                    {erroFator}
                  </p>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-800 border-b pb-2">Cabine</h3>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="celula-kanban-eh-cabine"
                    checked={form.ehCabine}
                    disabled={!podeEditar}
                    onCheckedChange={(marcado) => atualizar({ ehCabine: marcado === true })}
                  />
                  <Label htmlFor="celula-kanban-eh-cabine" className="text-xs font-semibold text-slate-700">
                    É cabine de pintura
                  </Label>
                </div>

                {form.ehCabine && (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Label htmlFor="celula-kanban-estufa" className="text-xs font-semibold text-slate-700">
                        Estufa vinculada
                      </Label>
                      <CampoSelect
                        id="celula-kanban-estufa"
                        value={form.estufaId}
                        disabled={!podeEditar}
                        onValueChange={(valor) => atualizar({ estufaId: valor })}
                        options={[
                          { value: '', label: 'Sem estufa' },
                          ...estufasVisiveis.map((e) => ({ value: e.id, label: `${e.codigo} - ${e.nome}${e.ativo ? '' : ' (inativa)'}`, disabled: !e.ativo })),
                        ]}
                      />
                    </div>

                    <fieldset
                      className="space-y-2"
                      aria-describedby={erroRegras?.campo === 'tintasAceitas' ? 'celula-kanban-erro-tinta' : undefined}
                    >
                      <legend className="text-xs font-semibold text-slate-700">Tipos de tinta aceitos</legend>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {TINTAS_CABINE.map((tinta, indice) => (
                          <div key={tinta.value} className="flex items-center gap-2">
                            <Checkbox
                              id={`celula-kanban-tinta-${tinta.value}`}
                              ref={indice === 0 ? primeiraTintaRef : undefined}
                              checked={form.tintasAceitas.includes(tinta.value)}
                              disabled={!podeEditar}
                              aria-invalid={erroRegras?.campo === 'tintasAceitas' || undefined}
                              onCheckedChange={(marcado) =>
                                atualizar({
                                  tintasAceitas: alternar<TipoTinta>(form.tintasAceitas, tinta.value, marcado === true),
                                })
                              }
                            />
                            <Label htmlFor={`celula-kanban-tinta-${tinta.value}`} className="text-xs text-slate-700">
                              {tinta.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {erroRegras?.campo === 'tintasAceitas' && (
                        <FormMessage id="celula-kanban-erro-tinta">{erroRegras.mensagem}</FormMessage>
                      )}
                    </fieldset>

                    <fieldset
                      className="space-y-2"
                      aria-describedby={erroRegras?.campo === 'familiasCorAceitas' ? 'celula-kanban-erro-familia' : undefined}
                    >
                      <legend className="text-xs font-semibold text-slate-700">Famílias de cor aceitas</legend>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="celula-kanban-cor-todas"
                            checked={form.aceitaTodasCores}
                            disabled={!podeEditar}
                            onCheckedChange={(marcado) => atualizar({ aceitaTodasCores: marcado === true })}
                          />
                          <Label htmlFor="celula-kanban-cor-todas" className="text-xs text-slate-700">
                            Todas
                          </Label>
                        </div>
                        {FAMILIAS_COR.map((familia, indice) => (
                          <div key={familia.value} className="flex items-center gap-2">
                            <Checkbox
                              id={`celula-kanban-cor-${familia.value}`}
                              ref={indice === 0 ? primeiraFamiliaRef : undefined}
                              checked={form.aceitaTodasCores || form.familiasCorAceitas.includes(familia.value)}
                              disabled={!podeEditar || form.aceitaTodasCores}
                              aria-invalid={erroRegras?.campo === 'familiasCorAceitas' || undefined}
                              onCheckedChange={(marcado) =>
                                atualizar({
                                  familiasCorAceitas: alternar<FamiliaCor>(
                                    form.familiasCorAceitas,
                                    familia.value,
                                    marcado === true,
                                  ),
                                })
                              }
                            />
                            <Label htmlFor={`celula-kanban-cor-${familia.value}`} className="text-xs text-slate-700">
                              {familia.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {erroRegras?.campo === 'familiasCorAceitas' && (
                        <FormMessage id="celula-kanban-erro-familia">{erroRegras.mensagem}</FormMessage>
                      )}
                    </fieldset>

                    <div className="space-y-1">
                      <Label htmlFor="celula-kanban-porte" className="text-xs font-semibold text-slate-700">
                        Porte máximo
                      </Label>
                      <CampoSelect
                        id="celula-kanban-porte"
                        value={form.porteMaximo}
                        disabled={!podeEditar}
                        onValueChange={(valor) => atualizar({ porteMaximo: valor as PortePeca | '' })}
                        options={[{ value: '', label: 'Não definido' }, ...PORTES_CABINE.map((porte) => ({ value: porte.value, label: porte.label }))]}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="celula-kanban-isolamento"
                        checked={form.aceitaIsolamento}
                        disabled={!podeEditar}
                        onCheckedChange={(marcado) => atualizar({ aceitaIsolamento: marcado === true })}
                      />
                      <Label htmlFor="celula-kanban-isolamento" className="text-xs font-semibold text-slate-700">
                        Aceita isolamento
                      </Label>
                    </div>

                    <p className="text-[11px] text-slate-500">
                      Regras só geram sugestão e alerta no quadro; nunca bloqueiam a movimentação.
                    </p>
                  </div>
                )}
              </section>
            </div>
          )}
        </FormDialogBody>

        <FormDialogFooter>
          <button
            type="button"
            onClick={fechar}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-md transition-colors"
          >
            {podeEditar ? 'Cancelar' : 'Fechar'}
          </button>
          {podeEditar && (
            <button
              type="button"
              onClick={() => void handleSalvar()}
              disabled={!podeGravarFormulario({ loading, cargaOk, saving })}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          )}
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
};
